import React, { useState, useEffect, useContext } from 'react';
import { Modal } from '../Common/Modal';
import { ConfirmDialog } from '../Common/ConfirmDialog';
import { FiSave, FiX } from 'react-icons/fi';
import visitService from '../../services/visitService';
import api from '../../services/api';
import { VISIT_STATUS } from '../../utils/constants';
import { useProject } from '../../context/ProjectContext';
import { AuthContext } from '../../context/AuthContext';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { visits as visitMessages, common as commonMessages } from '../../utils/validationMessages';
import VisitModalHeader from './VisitModalHeader';
import MultiSelectDropdown from '../Common/MultiSelectDropdown';
import SearchableSelect from '../Common/SearchableSelect';
import './ScheduleVisitModal.css';

/** Way2: CRA select value is mapping PK (optionId); legacy "userId_profileCode" still parsed. */
function craOptionValueToMappingId(optionOrMappingId, crasList) {
  const s = String(optionOrMappingId || '').trim();
  if (!s) return '';
  const list = crasList || [];
  const byOption = list.find((c) => String(c.optionId) === s || String(c.mappingId) === s);
  if (byOption) return String(byOption.mappingId || byOption.optionId);
  if (!s.includes('_')) return s;
  const uid = s.slice(0, s.indexOf('_'));
  const profile = s.slice(s.indexOf('_') + 1).toUpperCase();
  const match = list.find(
    (c) => String(c.userId || c.id) === uid && String(c.profileCode || '').toUpperCase() === profile,
  );
  return match ? String(match.mappingId || match.optionId) : '';
}

function craOptionValueToUserId(optionOrUserId, crasList) {
  const s = String(optionOrUserId || '').trim();
  if (!s) return '';
  const list = crasList || [];
  const byOption = list.find((c) => String(c.optionId) === s || String(c.mappingId) === s);
  if (byOption) return String(byOption.userId || byOption.id);
  const i = s.indexOf('_');
  return i > 0 ? s.slice(0, i) : s;
}

function craOptionValueToProfileCode(optionOrUserId, crasList) {
  const s = String(optionOrUserId || '').trim();
  if (!s) return '';
  const list = crasList || [];
  const byOption = list.find((c) => String(c.optionId) === s || String(c.mappingId) === s);
  if (byOption) return String(byOption.profileCode || 'CRA').toUpperCase();
  const i = s.indexOf('_');
  if (i > 0) return s.slice(i + 1).trim().toUpperCase() || 'CRA';
  return 'CRA';
}

/** Resolve visit/site CRA value → dropdown optionId (mapping PK). */
function resolveCraOptionIdFromList(crasList, mappingOrUserId, preferredProfileCode = 'CRA') {
  if (mappingOrUserId == null || mappingOrUserId === '') return '';
  const raw = String(mappingOrUserId).trim();
  if (!raw) return '';
  const list = crasList || [];
  const byMapping = list.find((c) => String(c.optionId) === raw || String(c.mappingId) === raw);
  if (byMapping) return byMapping.optionId;
  const uid = craOptionValueToUserId(raw, list);
  const pref = (preferredProfileCode || 'CRA').toString().trim().toUpperCase() || 'CRA';
  const exact = list.find(
    (c) => String(c.userId || c.id) === uid && String(c.profileCode || '').toUpperCase() === pref,
  );
  if (exact) return exact.optionId;
  const any = list.find((c) => String(c.userId || c.id) === uid);
  return any ? any.optionId : '';
}

/** User id from co-monitor option id (composite or legacy numeric). */
function parseUserIdFromCoMonitorOptionId(coMonitorID) {
  const s = String(coMonitorID);
  const i = s.indexOf('_');
  if (i > 0) {
    const uid = parseInt(s.slice(0, i), 10);
    return Number.isNaN(uid) ? null : uid;
  }
  const uid = parseInt(s, 10);
  return Number.isNaN(uid) ? null : uid;
}

/** API co-monitor option id: "nUserId_nUserRoleProjectSiteMapping" (one row per role mapping). */
function coMonitorOptionValuesToUserIds(optionValues) {
  const set = new Set();
  for (const v of optionValues || []) {
    const s = String(v);
    const i = s.indexOf('_');
    if (i > 0) {
      const uid = parseInt(s.slice(0, i), 10);
      if (!Number.isNaN(uid)) set.add(uid);
    } else {
      const uid = parseInt(s, 10);
      if (!Number.isNaN(uid)) set.add(uid);
    }
  }
  return Array.from(set);
}

function isCraFamilyCoMonitor(cm) {
  if (cm?.isCraFamily === true) return true;
  const code = String(cm?.userTypeCode || '').trim().toUpperCase();
  return code === 'CRA' || code === 'UCRA';
}

/**
 * Co-Monitor row allowed:
 * - Not the primary CRA's CRA-family mapping (same person may still appear as PM/SRM lane for co-monitor).
 * - Not a CRA-family row for users listed as site-assigned CRAs (avoid duplicate CRA on site).
 */
function isCoMonitorOptionAllowed(cm, site, primaryCraUserId, crasList) {
  const cra = String(primaryCraUserId || '').trim();
  const craFamily = isCraFamilyCoMonitor(cm);
  if (cra) {
    const uid = parseUserIdFromCoMonitorOptionId(cm.coMonitorID);
    const id = String(cm.coMonitorID);
    const sameUserAsPrimaryCra = uid != null && String(uid) === cra;
    if (sameUserAsPrimaryCra && craFamily) {
      return false;
    }
    if (id === cra && !id.includes('_')) {
      return false;
    }
  }
  const assigned = site?.assignedCraUserIds;
  if (!Array.isArray(assigned) || assigned.length === 0) return true;
  // Way2: assignedCraUserIds are mapping PKs → resolve to user ids via CRA list
  const assignedUserIds = new Set();
  for (const mid of assigned) {
    const row = (crasList || []).find(
      (c) => String(c.optionId) === String(mid) || String(c.mappingId) === String(mid),
    );
    assignedUserIds.add(row ? String(row.userId || row.id) : String(mid));
  }
  if (!craFamily) return true;
  const cmUid = parseUserIdFromCoMonitorOptionId(cm.coMonitorID);
  if (cmUid == null) return true;
  return !assignedUserIds.has(String(cmUid));
}

/** YYYY-MM-DD from API date string or date input. */
function extractScheduleYmd(dateVal) {
  if (dateVal == null) return '';
  const s = String(dateVal).trim();
  if (!s) return '';
  const part = s.includes('T') ? s.split('T')[0] : s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(part) ? part : '';
}

/** Inclusive calendar overlap on YYYY-MM-DD strings. */
function scheduleDateRangesOverlap(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart <= bEnd && bStart <= aEnd;
}

function visitStatusNormForOverlap(v) {
  return String(v?.visitStatus ?? v?.VisitStatus ?? '').trim().toLowerCase();
}

/** Count visit for CRA overlap if it is active, has planned range, and is not completed/cancelled. */
function isVisitConsideredForCraOverlap(v) {
  if (v?.isActive === false || v?.IsActive === false) return false;
  const st = visitStatusNormForOverlap(v);
  if (st === 'cancelled' || st === 'completed') return false;
  return true;
}

/** Resolve visit CRA_UserID (mapping PK) to UserMst.nUserId for overlap checks. */
function getVisitCraUserId(v, crasList) {
  const x = v?.cra_UserID ?? v?.CRA_UserID ?? v?.craID ?? v?.Cra_UserID ?? v?.CraUserID;
  if (x == null || x === '') return NaN;
  const raw = String(x).trim();
  const row = (crasList || []).find(
    (c) => String(c.optionId) === raw || String(c.mappingId) === raw,
  );
  if (row) {
    const n = parseInt(String(row.userId || row.id), 10);
    return Number.isNaN(n) ? NaN : n;
  }
  const n = typeof x === 'number' ? x : parseInt(raw, 10);
  return Number.isNaN(n) ? NaN : n;
}

function getScheduledVisitId(v) {
  return Number(v?.scheduledVisitID ?? v?.scheduledVisitId ?? v?.ScheduledVisitID ?? 0) || 0;
}

/** Map saved visit user ids to current dropdown option values (composite ids). Prefers a row still allowed for the site (e.g. PM over CRA). */
function mapSavedCoMonitorUserIdsToOptionValues(savedIds, coMonitorRows, site, primaryCraId) {
  if (!Array.isArray(savedIds) || savedIds.length === 0) return [];
  const rows = coMonitorRows || [];
  if (rows.length === 0) return savedIds.map(String).filter(Boolean);
  return savedIds
    .map((uid) => {
      const u = String(uid);
      const candidates = rows.filter((cm) => String(cm.coMonitorID).startsWith(`${u}_`));
      if (candidates.length === 0) return u;
      const allowed = candidates.filter((cm) => isCoMonitorOptionAllowed(cm, site, primaryCraId, null));
      const hit = allowed[0] || candidates[0];
      return hit ? hit.coMonitorID : u;
    })
    .filter(Boolean);
}

const ScheduleVisitModal = ({
  isOpen,
  onClose,
  selectedDate,
  onSave,
  defaultSite,
  mode = 'create', // 'create' | 'edit'
  existingVisit = null,
  plannerMode = false, // When true: Visit Type locked to SMV (disabled)
  confirmAfterSave = false, // When true (planner "Confirm Schedule"): after create, call confirm API so visit is Confirmed; later "Modify" will show view-only
}) => {
  const { user } = useContext(AuthContext);
  const { currentProject: contextProject, updateProject } = useProject();
  // When opening in edit mode, fetch latest visit from API so we have definitive VisitStatus (Confirmed => view-only)
  const [resolvedVisit, setResolvedVisit] = useState(null);

  const [siteVisitTypes, setSiteVisitTypes] = useState([]);
  /** True while schedule-visit-types is in flight; avoids empty-state error flash on site change. */
  const [siteVisitTypesLoading, setSiteVisitTypesLoading] = useState(false);
  const [formData, setFormData] = useState({
    projectCode: '',
    siteID: '',
    visitType: '',
    craID: '',
    coMonitorIds: [],
    plannedStartDate: '',
    plannedEndDate: '',
    notes: '',
    visitMode: '',
    visitStatus: VISIT_STATUS.DRAFT,
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  /** Inline validation under schedule form fields. Onboarding gate messages use toast only (see getScheduleVisitFieldErrors). */
  const [scheduleFieldErrors, setScheduleFieldErrors] = useState({});
  const [sites, setSites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [cras, setCras] = useState([]);
  const [coMonitors, setCoMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmInactive, setConfirmInactive] = useState({ open: false });
  const [confirmCraOverlap, setConfirmCraOverlap] = useState({ open: false });
  const [toast, setToast] = useState(null);

  // Modify Visit Schedule: Site cannot be changed.
  // Visit Scheduling Planner only: if visit exists in DB (we got it from API), show only details (no modify).
  // Other flows (e.g. Visit Management): view-only only when visit status is Confirmed.
  const isEditMode = mode === 'edit';
  const visitForStatus = resolvedVisit ?? existingVisit;
  const visitStatusRaw =
    visitForStatus?.visitStatus ?? visitForStatus?.VisitStatus ?? visitForStatus?.confirmationStatus ?? visitForStatus?.ConfirmationStatus ?? '';
  const isConfirmedStatus =
    String(visitStatusRaw).trim().toLowerCase() === 'confirmed';
  const isConfirmedView = isEditMode && (existingVisit || resolvedVisit) && isConfirmedStatus;
  const siteDisabled = isEditMode || isConfirmedView;
  const formReadOnly =
    (plannerMode && isEditMode && resolvedVisit != null) || (!plannerMode && isConfirmedView);

  // CRA-family (CRA or UCRA / Unblinded CRA) locks CRA dropdown.
  // Autofill prefers this user's site-assigned mapping matching the session profile.
  const profileCode = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? user?.Role ?? '').toString().trim().toUpperCase();
  const isCraFamilyProfile = profileCode === 'CRA' || profileCode === 'UCRA';
  const currentUserId = user?.iuid ?? user?.userId ?? user?.id ?? user?.Iuid ?? '';
  const currentUserIdStr = currentUserId ? String(currentUserId) : '';
  const craDropdownDisabled = formReadOnly || isCraFamilyProfile;

  /**
   * Way2: site.assignedCraUserIds = mapping PKs.
   * CRA-family session → only exact user + profile (CRA/UCRA) mapping if assigned; else '' (Select CRA).
   * Other profiles → first site-assigned mapping.
   */
  const resolveDefaultCraIdForSite = (site, profileCodeHere, uidHere, crasList) => {
    if (!site) return '';
    const assigned = Array.isArray(site.assignedCraUserIds)
      ? site.assignedCraUserIds.map(String)
      : [];
    if (assigned.length === 0 && site.assignedCraUserId != null) {
      assigned.push(String(site.assignedCraUserId));
    }
    if (assigned.length === 0) return '';

    const code = (profileCodeHere || '').toString().trim().toUpperCase();
    const list = crasList || cras || [];
    const isAssignedMapping = (c) =>
      assigned.includes(String(c.optionId)) || assigned.includes(String(c.mappingId));

    if (uidHere && list.length > 0 && code) {
      const exact = list.find(
        (c) =>
          isAssignedMapping(c)
          && String(c.userId || c.id) === String(uidHere)
          && String(c.profileCode || '').toUpperCase() === code,
      );
      if (exact) return String(exact.mappingId || exact.optionId);
    }

    if (isCraFamilyProfile || code === 'CRA' || code === 'UCRA') {
      return '';
    }
    return assigned[0];
  };

  // Helper to format a Date as local YYYY-MM-DD without timezone shift
  const formatLocalDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // In edit mode, fetch latest visit from API so we have definitive VisitStatus (Confirmed => view-only, no Update button)
  useEffect(() => {
    if (!isOpen) {
      setResolvedVisit(null);
      return;
    }
    if (mode !== 'edit' || !existingVisit) return;
    const visitId = existingVisit.scheduledVisitID ?? existingVisit.scheduledVisitId ?? existingVisit.ScheduledVisitID;
    if (visitId == null) return;
    let cancelled = false;
    visitService.getById(visitId)
      .then((data) => {
        if (!cancelled && data) setResolvedVisit(data);
      })
      .catch(() => {
        if (!cancelled) setResolvedVisit(null);
      });
    return () => { cancelled = true; };
  }, [isOpen, mode, existingVisit?.scheduledVisitID ?? existingVisit?.scheduledVisitId]);

  const visitModeFromApi = (raw) => {
    if (raw == null || String(raw).trim() === '') return '';
    const s = String(raw).trim();
    if (/^onsite$/i.test(s)) return 'Onsite';
    if (/^remote$/i.test(s)) return 'Remote';
    return '';
  };

  // In edit mode, update form with visit-specific data from existingVisit
  // (e.g. Co-Monitor from SiteCoMonitorAssignments by ScheduledVisitID) so Co-Monitor dropdown shows assigned values
  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !existingVisit) return;
    const sel = existingVisit.coMonitor_SelectionIds ?? existingVisit.CoMonitor_SelectionIds;
    const coMonitorIdsFromVisit = existingVisit.coMonitor_UserIDs ?? existingVisit.CoMonitor_UserIDs;
    let coMonitorIds = [];
    if (Array.isArray(sel) && sel.length > 0) {
      coMonitorIds = sel.map(String).filter(Boolean);
    } else if (Array.isArray(coMonitorIdsFromVisit)) {
      coMonitorIds = coMonitorIdsFromVisit.map((id) => String(id)).filter(Boolean);
    }
    setFormData((prev) => ({
      ...prev,
      coMonitorIds,
      ...(existingVisit.notes != null && existingVisit.notes !== '' && { notes: existingVisit.notes }),
      ...(existingVisit.cra_UserID != null && { craID: String(existingVisit.cra_UserID) }),
      ...(() => {
        const vm = visitModeFromApi(existingVisit.visitMode ?? existingVisit.VisitMode);
        return vm ? { visitMode: vm } : {};
      })(),
    }));
  }, [isOpen, mode, existingVisit]);

  // After getById resolves, apply composite coMonitor_SelectionIds (or map legacy user ids once options load)
  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !resolvedVisit) return;
    const sel = resolvedVisit.coMonitor_SelectionIds ?? resolvedVisit.CoMonitor_SelectionIds;
    if (Array.isArray(sel) && sel.length > 0) {
      setFormData((prev) => ({ ...prev, coMonitorIds: sel.map(String).filter(Boolean) }));
      return;
    }
    const raw = resolvedVisit.coMonitor_UserIDs ?? resolvedVisit.CoMonitor_UserIDs;
    if (!Array.isArray(raw) || raw.length === 0) {
      setFormData((prev) => ({ ...prev, coMonitorIds: [] }));
      return;
    }
    if (coMonitors.length === 0) return;
    const siteRow = sites.find((s) => String(s.siteID) === String(resolvedVisit.siteID ?? resolvedVisit.SiteID));
    const craFromVisit = String(resolvedVisit.cra_UserID ?? resolvedVisit.Cra_UserID ?? '');
    setFormData((prev) => ({
      ...prev,
      coMonitorIds: mapSavedCoMonitorUserIdsToOptionValues(
        raw.map((id) => String(id)).filter(Boolean),
        coMonitors,
        siteRow,
        craFromVisit || craOptionValueToUserId(prev.craID, cras),
      ),
    }));
  }, [isOpen, mode, resolvedVisit, coMonitors, sites]);

  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !resolvedVisit) return;
    const norm = visitModeFromApi(resolvedVisit.visitMode ?? resolvedVisit.VisitMode);
    if (!norm) return;
    setFormData((prev) => ({ ...prev, visitMode: norm }));
  }, [isOpen, mode, resolvedVisit]);

  // Load visit types for selected site (Way2 site assignment). Loading flag prevents empty-state error flash.
  useEffect(() => {
    if (!isOpen || !formData.siteID || plannerMode) {
      setSiteVisitTypes([]);
      setSiteVisitTypesLoading(false);
      return;
    }
    const projectCode = (formData.projectCode || '').trim();
    if (!projectCode) {
      setSiteVisitTypes([]);
      setSiteVisitTypesLoading(false);
      return;
    }
    let cancelled = false;
    setSiteVisitTypes([]);
    setSiteVisitTypesLoading(true);
    (async () => {
      try {
        const res = await api.get(`/sites/${formData.siteID}/schedule-visit-types`, {
          params: { projectCode },
        });
        if (!cancelled) setSiteVisitTypes(res.data || []);
      } catch {
        if (!cancelled) setSiteVisitTypes([]);
      } finally {
        if (!cancelled) setSiteVisitTypesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, formData.siteID, formData.projectCode, plannerMode]);

  // Expand plain CRA user id → optionId once CRA/UCRA list is loaded (prefer saved visit profile).
  useEffect(() => {
    if (!isOpen || !formData.craID || cras.length === 0) return;
    const cur = String(formData.craID);
    if (cur.includes('_')) return;
    const fromVisit = (
      existingVisit?.cra_ProfileCode
      ?? existingVisit?.CRA_ProfileCode
      ?? resolvedVisit?.cra_ProfileCode
      ?? resolvedVisit?.CRA_ProfileCode
      ?? ''
    ).toString().trim().toUpperCase();
    const preferred = fromVisit || (profileCode === 'UCRA' ? 'UCRA' : 'CRA');
    const optionId = resolveCraOptionIdFromList(cras, cur, preferred);
    if (optionId && optionId !== cur) {
      setFormData((prev) => (String(prev.craID) === cur ? { ...prev, craID: optionId } : prev));
    }
  }, [isOpen, formData.craID, cras, profileCode, existingVisit, resolvedVisit]);

  useEffect(() => {
    if (!isOpen || !formData.siteID || coMonitors.length === 0) return;
    const site = sites.find((s) => String(s.siteID) === String(formData.siteID));
    const primaryCraUserId = craOptionValueToUserId(formData.craID, cras);
    const allowed = new Set(
      coMonitors
        .filter((cm) => isCoMonitorOptionAllowed(cm, site, primaryCraUserId, cras))
        .map((cm) => String(cm.coMonitorID)),
    );
    setFormData((prev) => {
      const cur = prev.coMonitorIds || [];
      const next = cur.map(String).filter((id) => allowed.has(id));
      if (next.length === cur.length) return prev;
      return { ...prev, coMonitorIds: next };
    });
  }, [isOpen, formData.siteID, formData.craID, coMonitors, sites]);

  useEffect(() => {
    if (isOpen) {
      setScheduleFieldErrors({});
      const fromContext = contextProject && contextProject !== 'all' ? contextProject : null;
      const fromVisit =
        existingVisit &&
        String(existingVisit.projectCode || existingVisit.ProjectCode || '').trim();
      const defaultProjectCode = fromContext || fromVisit || null;
      fetchProjectsAndSites(defaultProjectCode).then(({ sitesList, coMonitorsList, crasList }) => {
        const toCraOption = (userId, profilePref = 'CRA') =>
          resolveCraOptionIdFromList(crasList || [], userId, profilePref);

        // For edit mode, pre-fill from existing visit
        if (mode === 'edit' && existingVisit) {
          const dateStart = existingVisit.plannedStartDate
            ? existingVisit.plannedStartDate.split('T')[0]
            : '';
          const dateEnd = existingVisit.plannedEndDate
            ? existingVisit.plannedEndDate.split('T')[0]
            : dateStart;
          const confirmationStatusRaw = (existingVisit.confirmationStatus ?? existingVisit.ConfirmationStatus ?? '').toString().trim().toLowerCase();
          const isRejectedByPI = confirmationStatusRaw === 'rejected';
          const siteForCoMonitor = sitesList?.find(s => String(s.siteID) === String(existingVisit.siteID));
          const craIdFromVisit = existingVisit.cra_UserID ? existingVisit.cra_UserID.toString() : '';
          const craProfileFromVisit = (
            existingVisit.cra_ProfileCode ?? existingVisit.CRA_ProfileCode ?? ''
          ).toString().trim().toUpperCase();
          const profileCodeHere = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? user?.Role ?? '').toString().trim().toUpperCase();
          const uidHere = (user?.iuid ?? user?.userId ?? user?.id ?? user?.Iuid ?? '').toString();
          const defaultCraUserFromSite = (!craIdFromVisit && siteForCoMonitor)
            ? resolveDefaultCraIdForSite(siteForCoMonitor, profileCodeHere, uidHere, crasList)
            : '';
          const craOption = toCraOption(
            craIdFromVisit || defaultCraUserFromSite,
            craProfileFromVisit || profileCodeHere || 'CRA',
          );
          // Co-Monitor: use only this visit's saved IDs, never from site/previous visit
          const selVisit = existingVisit.coMonitor_SelectionIds ?? existingVisit.CoMonitor_SelectionIds;
          const coMonitorIdsFromVisit = (existingVisit.coMonitor_UserIDs ?? existingVisit.CoMonitor_UserIDs);
          const coMonitorIdsRaw = (isRejectedByPI || !Array.isArray(coMonitorIdsFromVisit))
            ? []
            : coMonitorIdsFromVisit.map(id => String(id)).filter(Boolean);
          const coMonitorIds = (Array.isArray(selVisit) && selVisit.length > 0)
            ? selVisit.map(String).filter(Boolean)
            : mapSavedCoMonitorUserIdsToOptionValues(
                coMonitorIdsRaw,
                coMonitorsList,
                siteForCoMonitor,
                craOptionValueToUserId(craOption, cras) || craIdFromVisit || defaultCraUserFromSite || '',
              );
          setFormData(prev => ({
            ...prev,
            projectCode: defaultProjectCode || prev.projectCode,
            siteID: existingVisit.siteID ? existingVisit.siteID.toString() : '',
            visitType: plannerMode ? 'SMV' : (existingVisit.visitType || ''),
            craID: craOption || prev.craID,
            coMonitorIds,
            plannedStartDate: dateStart,
            plannedEndDate: dateEnd,
            notes: (existingVisit.notes != null && existingVisit.notes !== '') ? existingVisit.notes : prev.notes,
            visitMode: visitModeFromApi(existingVisit.visitMode ?? existingVisit.VisitMode) || prev.visitMode,
            visitStatus: existingVisit.visitStatus || prev.visitStatus,
          }));
          return;
        }

        // Planner mode create: pre-fill from suggested slot (existingVisit has siteID, studyId, plannedStartDate, plannedEndDate)
        if (plannerMode && existingVisit) {
          const todayStr = formatLocalDate(new Date());
          let dateStart = existingVisit.plannedStartDate
            ? String(existingVisit.plannedStartDate).split('T')[0]
            : '';
          let dateEnd = existingVisit.plannedEndDate
            ? String(existingVisit.plannedEndDate).split('T')[0]
            : dateStart;
          if (dateStart && dateStart < todayStr) {
            dateStart = todayStr;
            dateEnd = todayStr;
          } else if (dateEnd && dateEnd < todayStr) {
            dateEnd = dateStart || todayStr;
          }
          const siteForCoMonitor = sitesList?.find(s => String(s.siteID) === String(existingVisit.siteID));
          const craIdFromVisit = existingVisit.cra_UserID ? String(existingVisit.cra_UserID) : '';
          const craProfileFromVisit = (
            existingVisit.cra_ProfileCode ?? existingVisit.CRA_ProfileCode ?? ''
          ).toString().trim().toUpperCase();
          const profileCodeHere = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? user?.Role ?? '').toString().trim().toUpperCase();
          const uidHere = (user?.iuid ?? user?.userId ?? user?.id ?? user?.Iuid ?? '').toString();
          const defaultCraUserFromSite = (!craIdFromVisit && siteForCoMonitor)
            ? resolveDefaultCraIdForSite(siteForCoMonitor, profileCodeHere, uidHere, crasList)
            : '';
          const craOption = toCraOption(
            craIdFromVisit || defaultCraUserFromSite,
            craProfileFromVisit || profileCodeHere || 'CRA',
          );
          // Co-Monitor: never default from site/previous visit for a new visit
          setFormData(prev => ({
            ...prev,
            projectCode: defaultProjectCode || prev.projectCode,
            siteID: existingVisit.siteID ? String(existingVisit.siteID) : '',
            visitType: 'SMV',
            craID: craOption || prev.craID,
            coMonitorIds: [],
            plannedStartDate: dateStart,
            plannedEndDate: dateEnd || dateStart,
            notes: prev.notes,
            visitMode: visitModeFromApi(existingVisit.visitMode ?? existingVisit.VisitMode) || '',
            visitStatus: VISIT_STATUS.DRAFT,
          }));
          return;
        }

        // CREATE mode default values after data is loaded
        const todayStr = formatLocalDate(new Date());
        const dateStr = selectedDate ? formatLocalDate(selectedDate) : '';
        const useDate = (dateStr && dateStr >= todayStr) ? dateStr : todayStr;

        // Find site ID from defaultSite and default CRA mapping for session profile (CRA-family inclusive)
        let defaultSiteID = '';
        let defaultCraID = '';
        const profileCodeHere = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? user?.Role ?? '').toString().trim().toUpperCase();
        const uidHere = (user?.iuid ?? user?.userId ?? user?.id ?? user?.Iuid ?? '').toString();
        if (defaultSite && defaultSite !== 'all' && sitesList && sitesList.length > 0) {
          const site = sitesList.find(s => s.siteName === defaultSite || s.legacyName === defaultSite);
          if (site) {
            defaultSiteID = site.siteID;
            defaultCraID = toCraOption(
              resolveDefaultCraIdForSite(site, profileCodeHere, uidHere, crasList),
              profileCodeHere || 'CRA',
            );
          }
        }
        // Co-Monitor: never default from site/previous visit when starting a new visit
        setFormData(prev => ({
          ...prev,
          projectCode: defaultProjectCode || '',
          siteID: defaultSiteID || '',
          craID: defaultCraID || prev.craID || '',
          coMonitorIds: [],
          plannedStartDate: useDate,
          plannedEndDate: useDate,
          visitMode: '',
        }));
      });
    } else {
      // Reset form when modal closes
      const todayStr = formatLocalDate(new Date());
      const dateStr = selectedDate ? formatLocalDate(selectedDate) : '';
      const useDate = (dateStr && dateStr >= todayStr) ? dateStr : todayStr;
      setFormData({
        projectCode: '',
        siteID: '',
        visitType: '',
        craID: '',
        coMonitorIds: [],
        plannedStartDate: useDate,
        plannedEndDate: useDate,
        notes: '',
        visitMode: '',
        visitStatus: VISIT_STATUS.DRAFT,
      });
      setError(null);
      setScheduleFieldErrors({});
    }
  }, [isOpen, selectedDate, defaultSite, mode, existingVisit, plannerMode, contextProject, user]);

  const pruneScheduleFieldErrorsForInput = (name) => {
    setScheduleFieldErrors((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const keysToClear = [];
      if (name === 'projectCode') keysToClear.push('projectCode', 'siteID', 'craID', 'visitType');
      else if (name === 'siteID') keysToClear.push('siteID', 'craID', 'visitType');
      else if (name === 'craID') keysToClear.push('craID');
      else if (name === 'visitType') keysToClear.push('visitType');
      else if (name === 'visitMode') keysToClear.push('visitMode');
      else if (name === 'plannedStartDate') keysToClear.push('plannedStartDate');
      else if (name === 'plannedEndDate') keysToClear.push('plannedEndDate');
      else keysToClear.push(name);
      const next = { ...prev };
      let changed = false;
      keysToClear.forEach((k) => {
        if (next[k]) {
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const fetchProjectsAndSites = async (selectedProjectCode = null) => {
    try {
      setLoading(true);
      setError(null);

      const currentProject = selectedProjectCode || (contextProject && contextProject !== 'all' ? contextProject : null);
      const coMonitorsRequest =
        currentProject && currentProject !== 'all'
          ? api.get('/users/co-monitors', { params: { project: currentProject } })
          : api.get('/users/co-monitors');

      // Fetch projects from ProjectMst and CRAs + Co-Monitors (Co-Monitors scoped to session / selected project on API)
      const [projectsResponse, crasResponse, coMonitorsResponse] = await Promise.all([
        api.get('/ImportData/GetProject'), // Fetch from ProjectMst
        api.get('/users/cras', currentProject ? { params: { project: currentProject } } : {}),
        coMonitorsRequest,
      ]);

      // Map projects from ProjectMst
      const projectsList = (projectsResponse.data || [])
        .filter(project => project.isActive !== false && project.IsActive !== false) // Only active projects
        .map(project => ({
          projectCode: project.projectCode || project.ProjectCode || '',
          // Phase 6: ProjectNo is canonical; keep projectId as fallback for older payloads.
          projectNo: project.projectNo ?? project.ProjectNo ?? project.nProjectNo ?? null,
          projectId:
            project.projectId ??
            project.ProjectId ??
            project.nProjectId ??
            project.projectNo ??
            project.ProjectNo ??
            null,
        }))
        .filter(project => project.projectCode) // Filter out empty project codes
        .sort((a, b) => a.projectCode.localeCompare(b.projectCode));
      
      // Fetch sites: CRA-assigned only for Schedule New Visit. Include sites after SCV completion (API still enforces visit-type rules on save).
      let sitesResponse;
      if (currentProject && currentProject !== 'all') {
        sitesResponse = await api.get(`/sites?project=${currentProject}&onlyWithCRAAssignment=true`);
      } else {
        // Fetch all active sites (no project selected - no CRA filter)
        sitesResponse = await api.get('/sites');
      }

      // Map sites: display as vSiteCode + vSiteDescription; keep assignedCraUserId/assignedCraName for default CRA
      const sitesList = (sitesResponse.data || [])
        .map(site => {
          const siteCode =
            site.vSiteCode || site.VSiteCode ||
            site.siteCode || site.SiteCode ||
            '';
          const siteDescription =
            site.sitedescription || site.siteDescription || site.SiteDescription ||
            '';
          const siteDisplay = [siteCode, siteDescription].filter(Boolean).join(' - ') || siteCode || siteDescription;
          const siteNo = site.siteNo ?? site.SiteNo ?? site.siteID ?? 0;
          return {
            siteID: site.siteID ?? site.siteId,
            siteNo: Number(siteNo) || 0,
            siteName: siteDisplay,
            siteCode,
            siteDescription,
            legacyName: siteDescription || siteCode,
            projectCode: site.projectCode || null,
            piName: site.piName ?? site.investigator ?? '',
            isSiteOnboarding: !!(site.isSiteOnboarding ?? site.IsSiteOnboarding),
            assignedCraUserId: site.assignedCraUserId != null ? String(site.assignedCraUserId) : undefined,
            assignedCraName: site.assignedCraName || undefined,
            assignedCraUserIds: Array.isArray(site.assignedCraUserIds) ? site.assignedCraUserIds.map(id => String(id)) : (site.assignedCraUserId != null ? [String(site.assignedCraUserId)] : []),
            assignedCoMonitorUserId: site.assignedCoMonitorUserId != null ? String(site.assignedCoMonitorUserId) : undefined,
            assignedCoMonitorName: site.assignedCoMonitorName || undefined,
            assignedCoMonitorUserIds: Array.isArray(site.assignedCoMonitorUserIds) ? site.assignedCoMonitorUserIds.map(id => String(id)) : (site.assignedCoMonitorUserId != null ? [String(site.assignedCoMonitorUserId)] : []),
          };
        })
        .filter(site => site.siteCode || site.siteName)
        .sort((a, b) => (a.siteNo || 0) - (b.siteNo || 0));

      // Way2: optionId = mapping PK; keep CRA + UCRA as separate options.
      const raw = crasResponse.data || [];
      const seen = new Set();
      const crasList = raw
        .map((cra) => {
          const userId = String(cra.id);
          const mappingId = String(cra.mappingId ?? cra.optionId ?? '');
          const profileCode = (cra.profileCode ?? cra.ProfileCode ?? 'CRA').toString().trim() || 'CRA';
          const optionId = String(cra.optionId || mappingId || `${userId}_${profileCode}`);
          return {
            userId,
            id: userId,
            mappingId: mappingId || optionId,
            optionId,
            profileCode,
            craName: cra.name || '',
          };
        })
        .filter((cra) => cra.craName && !seen.has(cra.optionId) && seen.add(cra.optionId));

      const coMonitorsRaw = coMonitorsResponse?.data || [];
      const seenCo = new Set();
      const coMonitorsList = coMonitorsRaw
        .map(cm => ({
          coMonitorID: String(cm.id),
          coMonitorName: cm.name || '',
          userTypeCode: cm.userTypeCode != null ? String(cm.userTypeCode) : '',
          isCraFamily: cm.isCraFamily === true,
        }))
        .filter(cm => cm.coMonitorName && !seenCo.has(cm.coMonitorID) && seenCo.add(cm.coMonitorID));

      setProjects(projectsList);
      setSites(sitesList);
      setCras(crasList);
      setCoMonitors(coMonitorsList);
      
      return { projectsList, sitesList, coMonitorsList, crasList };
    } catch (err) {
      console.error('Error fetching data:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load form data';
      setError(errorMessage);
      setProjects([]);
      setSites([]);
      setCras([]);
      setCoMonitors([]);
      return { projectsList: [], sitesList: [], coMonitorsList: [], crasList: [] };
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If projectCode is changed, refetch sites
    if (name === 'projectCode' && value) {
      const selectedProject = projects.find(p => p.projectCode === value);
      if (selectedProject) {
        updateProject(selectedProject.projectCode);
      }
      // Site cleared → no visit-types fetch until a site is chosen again
      setSiteVisitTypes([]);
      setSiteVisitTypesLoading(false);
      // Update form data with project code
      setFormData(prev => ({
        ...prev,
        projectCode: value,
        siteID: '', // Reset site when project changes
        visitType: '',
        craID: '', // Reset CRA until user selects a site
        coMonitorIds: [],
      }));
      
      // Refetch sites for the selected project
      fetchProjectsAndSites(value).then(({ sitesList }) => {
        // Sites are already updated via setSites in fetchProjectsAndSites
      });
    } else if (name === 'siteID') {
      // When site changes: CRA-family → own assigned mapping for session profile (CRA/UCRA); else first site CRA
      const site = sites.find(s => String(s.siteID) === String(value));
      const defaultCraMappingId = resolveDefaultCraIdForSite(site, profileCode, currentUserIdStr, cras);
      const defaultCraID = resolveCraOptionIdFromList(cras, defaultCraMappingId, profileCode || 'CRA');
      // Sync loading before paint so empty-state error never flashes while API runs
      setSiteVisitTypes([]);
      if (value) setSiteVisitTypesLoading(true);
      else setSiteVisitTypesLoading(false);
      // Co-Monitor: never default from site/previous visit for any visit type
      setFormData(prev => ({
        ...prev,
        siteID: value,
        visitType: '',
        craID: defaultCraID,
        coMonitorIds: [],
      }));
    } else if (name === 'craID') {
      // When CRA changes, remove CRA from Co-Monitor selection (CRA cannot be Co-Monitor)
      const craUserId = craOptionValueToUserId(value, cras);
      setFormData(prev => ({
        ...prev,
        craID: value,
        coMonitorIds: (prev.coMonitorIds || []).filter((id) => {
          const [uid] = coMonitorOptionValuesToUserIds([id]);
          return String(uid ?? id) !== String(craUserId);
        }),
      }));
    } else if (name === 'visitType') {
      setFormData(prev => ({ ...prev, visitType: value }));
    } else {
      // For other fields, just update normally
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
    
    if (error) setError(null);
    pruneScheduleFieldErrorsForInput(name);
  };

  const handleClear = () => {
    setError(null);
    setScheduleFieldErrors({});
    const todayStr = formatLocalDate(new Date());
    if (isEditMode && existingVisit) {
      // Modify Schedule Visit: clear only Notes, dates → today, Co-Monitor → first value. Keep Project, Site, Visit Type, CRA.
      setFormData((prev) => ({
        ...prev,
        notes: '',
        visitMode: '',
        coMonitorIds: [],
        plannedStartDate: todayStr,
        plannedEndDate: todayStr,
      }));
    } else {
      // Schedule New Visit: clear Notes, dates → today, Co-Monitor, Visit Type, Site → first value. Keep Project, CRA.
      setFormData((prev) => ({
        ...prev,
        siteID: '',
        craID: '',
        visitType: '',
        coMonitorIds: [],
        notes: '',
        visitMode: '',
        plannedStartDate: todayStr,
        plannedEndDate: todayStr,
      }));
    }
  };

  const resolveStudyIdForSubmit = () => {
    if (plannerMode && existingVisit?.studyID != null) return existingVisit.studyID;
    const p = projects.find((pr) => pr.projectCode === formData.projectCode);
    // Phase 6: backend treats StudyID as ProjectNo; fall back to legacy projectId if needed.
    return p?.projectNo ?? p?.projectId ?? null;
  };

  /**
   * Client checks before save. Field keys map to inline errors except onboarding rules
   * (SSV after onboard / SIV–SCV before onboard) — those return as onboardingToast for Alert only.
   */
  const getScheduleVisitFieldErrors = () => {
    const err = {};
    let onboardingToast = null;

    if (!siteDisabled && !formData.siteID) {
      err.siteID = visitMessages.siteRequired;
    }

    const visitTypeEffective = plannerMode
      ? (formData.visitType || 'SMV').trim()
      : (formData.visitType || '').trim();
    if (!visitTypeEffective) {
      err.visitType = isEditMode ? visitMessages.visitTypeRequiredAlt : visitMessages.visitTypeRequired;
    }

    if (!formData.craID) err.craID = visitMessages.craRequired;

    const vm = (formData.visitMode || '').trim();
    if (vm !== 'Onsite' && vm !== 'Remote') err.visitMode = visitMessages.visitModeRequired;

    const startReq = isEditMode ? visitMessages.startDateRequiredAlt : visitMessages.startDateRequired;
    const endReq = isEditMode ? visitMessages.endDateRequiredAlt : visitMessages.endDateRequired;
    const todayStr = formatLocalDate(new Date());

    let startMsg = null;
    if (!formData.plannedStartDate?.trim()) startMsg = startReq;
    else if (
      formData.plannedEndDate?.trim()
      && formData.plannedStartDate
      && new Date(formData.plannedStartDate) > new Date(formData.plannedEndDate)
    ) {
      startMsg = visitMessages.startAfterEnd;
    } else if (formData.plannedStartDate && formData.plannedStartDate < todayStr) {
      startMsg = visitMessages.visitDatePresentOrFuture;
    }

    let endMsg = null;
    if (!formData.plannedEndDate?.trim()) endMsg = endReq;
    else if (
      formData.plannedStartDate?.trim()
      && formData.plannedEndDate
      && new Date(formData.plannedStartDate) > new Date(formData.plannedEndDate)
    ) {
      endMsg = visitMessages.endBeforeStart;
    } else if (formData.plannedEndDate && formData.plannedEndDate < todayStr) {
      endMsg = visitMessages.visitDatePresentOrFuture;
    }

    if (startMsg) err.plannedStartDate = startMsg;
    if (endMsg) err.plannedEndDate = endMsg;

    const studyID = resolveStudyIdForSubmit();
    if (studyID == null) err.projectCode = visitMessages.projectStudyRequired;

    return { fieldErrors: err, onboardingToast };
  };

  /**
   * True if selected CRA has another visit (same project) with overlapping planned dates.
   * Skips current visit when editing. API errors do not block save.
   */
  const checkCraOverlapsAnotherVisit = async () => {
    const projectCode = (formData.projectCode || '').trim();
    const craIdStr = craOptionValueToUserId(formData.craID, cras);
    const newStart = extractScheduleYmd(formData.plannedStartDate);
    const newEnd = extractScheduleYmd(formData.plannedEndDate);
    if (!projectCode || !craIdStr || !newStart || !newEnd) return false;
    if (newStart > newEnd) return false;
    const craNum = parseInt(craIdStr, 10);
    if (Number.isNaN(craNum)) return false;

    let list = [];
    try {
      list = await visitService.getAll(projectCode);
    } catch {
      return false;
    }
    if (!Array.isArray(list)) return false;

    const editingId =
      mode === 'edit'
        ? Number(existingVisit?.scheduledVisitID ?? existingVisit?.scheduledVisitId ?? 0) || 0
        : 0;

    for (const v of list) {
      const vid = getScheduledVisitId(v);
      if (editingId > 0 && vid === editingId) continue;
      const vCra = getVisitCraUserId(v, cras);
      if (Number.isNaN(vCra) || vCra !== craNum) continue;
      if (!isVisitConsideredForCraOverlap(v)) continue;
      const vs = extractScheduleYmd(v.plannedStartDate ?? v.PlannedStartDate);
      const ve = extractScheduleYmd(v.plannedEndDate ?? v.PlannedEndDate);
      if (!vs || !ve) continue;
      if (scheduleDateRangesOverlap(newStart, newEnd, vs, ve)) return true;
    }
    return false;
  };

  const doSubmitVisit = async (vRemarks) => {
    const studyID = resolveStudyIdForSubmit();
    if (studyID == null) {
      setScheduleFieldErrors((prev) => ({ ...prev, projectCode: visitMessages.projectStudyRequired }));
      return;
    }
    const visitTypeForApi = plannerMode ? (formData.visitType || 'SMV') : formData.visitType;
    const visitData = {
      StudyID: parseInt(String(studyID), 10),
      SiteID: parseInt(String(formData.siteID), 10),
      VisitType: visitTypeForApi,
      CRA_UserID: (() => {
        // Way2: persist mapping PK in CRA_UserID
        const mid = craOptionValueToMappingId(formData.craID, cras);
        if (!mid) return null;
        const n = parseInt(mid, 10);
        return Number.isNaN(n) ? null : n;
      })(),
      coMonitor_SelectionIds: (formData.coMonitorIds || []).map(String).filter(Boolean),
      coMonitor_UserIDs: coMonitorOptionValuesToUserIds(formData.coMonitorIds || []),
      PlannedStartDate: formData.plannedStartDate,
      PlannedEndDate: formData.plannedEndDate,
      ConfirmationMode: 'Email',
      vRemarks: vRemarks || undefined,
      notes: (formData.notes || '').trim() || undefined,
      VisitMode: (formData.visitMode || '').trim(),
    };
    if (mode === 'edit' && existingVisit && existingVisit.scheduledVisitID) {
      await visitService.update(existingVisit.scheduledVisitID, visitData);
    } else {
      const created = await visitService.create(visitData);
      const createdId = created?.scheduledVisitID ?? created?.ScheduledVisitID;
      if (confirmAfterSave && createdId != null) {
        await visitService.confirm(createdId);
      }
    }
    if (onSave) onSave();
    onClose();
  };

  const submitVisitAfterValidation = () => {
    setSubmitting(true);
    doSubmitVisit('')
      .catch((err) => {
        if (err.response?.status === 401) return;
        const data = err.response?.data;
        const fromErrors = data?.errors && typeof data.errors === 'object'
          ? Object.values(data.errors).flat().find((x) => typeof x === 'string' && x)
          : null;
        const msg = data?.error || data?.message || fromErrors || err.message || 'Failed to schedule visit';
        setToast({ type: 'error', message: msg });
        console.error('Error scheduling visit:', err);
      })
      .finally(() => setSubmitting(false));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formReadOnly) return;
    setError(null);
    const { fieldErrors, onboardingToast } = getScheduleVisitFieldErrors();
    if (onboardingToast) {
      setToast({ type: 'error', message: onboardingToast });
    }
    if (Object.keys(fieldErrors).length > 0) {
      setScheduleFieldErrors(fieldErrors);
      return;
    }
    if (onboardingToast) {
      return;
    }
    setScheduleFieldErrors({});

    const hasOverlap = await checkCraOverlapsAnotherVisit();
    if (hasOverlap) {
      setConfirmCraOverlap({
        open: true,
        onConfirm: () => {
          setConfirmCraOverlap({ open: false });
          submitVisitAfterValidation();
        },
      });
      return;
    }

    submitVisitAfterValidation();
  };

  const handleMarkInactiveClick = () => {
    setConfirmInactive({
      open: true,
      message: visitMessages.inactiveConfirm,
      onConfirm: () => {
        setConfirmInactive({ open: false });
        void handleMarkInactive();
      },
    });
  };

  const handleMarkInactive = async () => {
    const id = existingVisit?.scheduledVisitID ?? existingVisit?.scheduledVisitId;
    if (!id) return;
    const remark = 'Cancelled by user';
    setSubmitting(true);
    setError(null);
    try {
      await visitService.cancel(id, { reason: remark, markInactive: true });
      if (onSave) onSave();
      setToast({ type: 'success', message: commonMessages.authSuccess });
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to cancel visit.';
      setToast({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const footer = formReadOnly ? (
    <button
      type="button"
      className="btn btn-secondary"
      onClick={onClose}
    >
      Close
    </button>
  ) : (
    <>
      {mode === 'edit' && existingVisit?.scheduledVisitID && (
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleMarkInactiveClick}
          disabled={submitting}
        >
          Cancel
        </button>
      )}
      <button
        type="submit"
        form="schedule-visit-form"
        className="btn btn-primary"
        disabled={submitting}
      >
        {submitting
          ? (confirmAfterSave ? 'Confirming...' : 'Saving...')
          : mode === 'edit'
            ? 'Save'
            : confirmAfterSave
              ? 'Confirm Schedule'
              : 'Save'}
      </button>
      <button
        type="button"
        className="btn btn-outline-secondary"
        onClick={handleClear}
        disabled={submitting}
      >
        Clear
      </button>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        disabled={submitting}
      >
        Close
      </button>
      
      
    </>
  );

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        formReadOnly
          ? (plannerMode ? 'Visit Details' : 'Visit Details (Confirmed)')
          : mode === 'edit'
            ? 'Modify Schedule Visit'
            : confirmAfterSave
              ? 'Confirm Visit Schedule'
              : 'Schedule New Visit'
      }
      size="large"
      footer={footer}
      className="schedule-visit-modal"
    >
      {loading ? (
        <Spinner />
      ) : (
        <form id="schedule-visit-form" onSubmit={handleSubmit} noValidate>
          <VisitModalHeader
            projectCode={formData.projectCode}
            siteName={sites.find(s => String(s.siteID) === String(formData.siteID))?.siteName}
            siteCode={sites.find(s => String(s.siteID) === String(formData.siteID))?.siteCode}
            piName={
              resolvedVisit?.piName ?? existingVisit?.piName ?? resolvedVisit?.PIName ?? existingVisit?.pIName ?? existingVisit?.PIName
              ?? (sites.find(s => String(s.siteID) === String(formData.siteID))?.piName) ?? ''
            }
          />
          {error && <Alert type="error" message={error} />}
          {formReadOnly && (
            <p className="form-readonly-notice" style={{ marginBottom: '16px', padding: '12px', background: '#f0f9ff', borderRadius: '8px', color: '#134252', fontSize: '14px' }}>
              {plannerMode
                ? 'This visit exists in the system. Details are shown for information only and cannot be modified.'
                : 'This visit has been confirmed and saved as an actual visit. Details are shown for information only and cannot be modified.'}
            </p>
          )}

          <div className="form-section">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Project</label>
                <select
                  name="projectCode"
                  value={formData.projectCode}
                  onChange={handleChange}
                  required
                  className="form-select"
                  disabled={formReadOnly || true}
                >
                  <option value="">Select Project</option>
                  {projects.map(project => (
                    <option key={project.projectCode} value={project.projectCode}>
                      {project.projectCode}
                    </option>
                  ))}
                </select>
                {scheduleFieldErrors.projectCode ? (
                  <p className="schedule-field-error" role="alert">{scheduleFieldErrors.projectCode}</p>
                ) : null}
              </div>

              <div className="form-group">
                <label className="form-label">Site <span class="required-asterisk">*</span></label>
                <SearchableSelect
                  name="siteID"
                  value={formData.siteID}
                  onChange={handleChange}
                  options={sites.map((site) => ({
                    value: site.siteID,
                    label: site.siteName,
                  }))}
                  placeholder="Select Site"
                  disabled={siteDisabled}
                  searchPlaceholder="Search by site code or name…"
                />
                {scheduleFieldErrors.siteID ? (
                  <p className="schedule-field-error" role="alert">{scheduleFieldErrors.siteID}</p>
                ) : null}
              </div>

              <div className="form-group">
                <label className="form-label">Visit Type <span class="required-asterisk">*</span></label>
                <select
                  name="visitType"
                  value={formData.visitType || (plannerMode ? 'SMV' : '')}
                  onChange={handleChange}
                  className="form-select"
                  disabled={isEditMode || plannerMode || formReadOnly}
                >
                  <option value="">
                    {!formData.siteID
                      ? 'Select a site first'
                      : siteVisitTypesLoading
                        ? 'Loading visit types…'
                        : siteVisitTypes.length === 0
                          ? 'No visit types assigned for this site'
                          : 'Select Visit Type'}
                  </option>
                  {siteVisitTypes.map((vt) => (
                    <option key={vt.visitCode} value={vt.visitCode}>
                      {vt.visitName}
                    </option>
                  ))}
                </select>
                {/* Empty assignment: dropdown placeholder only — no red line during/after load. Save still validates visit type. */}
                {scheduleFieldErrors.visitType ? (
                  <p className="schedule-field-error" role="alert">{scheduleFieldErrors.visitType}</p>
                ) : null}
              </div>

              <div className="form-group">
                <label className="form-label">CRA <span class="required-asterisk">*</span></label>
                <select
                  name="craID"
                  value={formData.craID}
                  onChange={handleChange}
                  className="form-select"
                  disabled={craDropdownDisabled}
                  title={isCraFamilyProfile ? 'CRA/UCRA is fixed to your assignment for the selected site' : undefined}
                >
                  <option value="">Select CRA</option>
                  {cras.map((cra) => (
                    <option key={cra.optionId} value={cra.optionId}>
                      {cra.craName}
                    </option>
                  ))}
                </select>
                {scheduleFieldErrors.craID ? (
                  <p className="schedule-field-error" role="alert">{scheduleFieldErrors.craID}</p>
                ) : null}
              </div>

              <div className="form-group">
                <label className="form-label">Co-Monitor</label>
                <MultiSelectDropdown
                  placeholder={formData.siteID ? 'Select Co-Monitor' : 'Select site first'}
                  options={formData.siteID
                    ? coMonitors
                        .filter((cm) =>
                          isCoMonitorOptionAllowed(
                            cm,
                            sites.find((s) => String(s.siteID) === String(formData.siteID)),
                            craOptionValueToUserId(formData.craID, cras),
                            cras,
                          ),
                        )
                        .map((cm) => ({ value: cm.coMonitorID, label: cm.coMonitorName }))
                    : []}
                  selectedValues={formData.coMonitorIds || []}
                  onChange={(ids) => setFormData(prev => ({ ...prev, coMonitorIds: ids || [] }))}
                  disabled={formReadOnly || !formData.siteID}
                  searchable
                  searchPlaceholder="Search co-monitor…"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Visit Mode <span class="required-asterisk">*</span></label>
                <select
                  name="visitMode"
                  value={formData.visitMode || ''}
                  onChange={handleChange}
                  className="form-select"
                  disabled={formReadOnly}
                  required
                >
                  <option value="">Select</option>
                  <option value="Onsite">Onsite</option>
                  <option value="Remote">Remote</option>
                </select>
                {scheduleFieldErrors.visitMode ? (
                  <p className="schedule-field-error" role="alert">{scheduleFieldErrors.visitMode}</p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="form-section">
              <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Scheduled Start Date <span class="required-asterisk">*</span></label>
                <input
                  type="date"
                  name="plannedStartDate"
                  value={formData.plannedStartDate}
                  min={formatLocalDate(new Date())}
                  onChange={handleChange}
                  className="form-input"
                  disabled={formReadOnly}
                />
                {scheduleFieldErrors.plannedStartDate ? (
                  <p className="schedule-field-error" role="alert">{scheduleFieldErrors.plannedStartDate}</p>
                ) : null}
              </div>

              <div className="form-group">
                <label className="form-label">Scheduled End Date <span class="required-asterisk">*</span></label>
                <input
                  type="date"
                  name="plannedEndDate"
                  value={formData.plannedEndDate}
                  min={(() => {
                    const todayStr = formatLocalDate(new Date());
                    return formData.plannedStartDate && formData.plannedStartDate >= todayStr
                      ? formData.plannedStartDate
                      : todayStr;
                  })()}
                  onChange={handleChange}
                  className="form-input"
                  disabled={formReadOnly}
                />
                {scheduleFieldErrors.plannedEndDate ? (
                  <p className="schedule-field-error" role="alert">{scheduleFieldErrors.plannedEndDate}</p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                maxLength={2000}
                placeholder="Enter Notes (max 2000 characters)"
                className="form-textarea"
                disabled={formReadOnly}
              />
            </div>
          </div>
        </form>
      )}
    </Modal>
    {confirmInactive.open && (
      <ConfirmDialog
        isOpen={true}
        onClose={() => setConfirmInactive({ open: false })}
        onConfirm={confirmInactive.onConfirm}
        title="Cancel visit"
        message={confirmInactive.message}
        confirmText="Yes"
        cancelText="No"
        variant="danger"
      />
    )}
    {confirmCraOverlap.open && (
      <ConfirmDialog
        isOpen={true}
        onClose={() => setConfirmCraOverlap({ open: false })}
        onConfirm={confirmCraOverlap.onConfirm}
        title={visitMessages.craOverlapScheduleConfirmTitle}
        message={visitMessages.craOverlapScheduleConfirmMessage}
        confirmText="Yes"
        cancelText="No"
        variant="warning"
        confirmButtonVariant="primary"
      />
    )}
    {toast && (
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
        <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      </div>
    )}
    </>
  );
};

export default ScheduleVisitModal;
